/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable consistent-return */
/* eslint-disable react/no-array-index-key */
import React, { memo, useRef, useEffect, useState, useMemo, JSX } from 'react';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/src/components/atoms/ItemsSwiper';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import SwiperPagination from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperPagination';
import { SwiperClass } from 'swiper/react';
import * as S from './style';

interface IProps {
  items?: React.JSX.Element | React.JSX.Element[];
  isDesktop?: boolean;
  onSlideChange?: (swiper: SwiperClass) => void;
  autoPlayEnabled?: boolean; // 모바일 autoplay 외부 제어 (기본값: true)
  onTransitionEnd?: (swiper: SwiperClass) => void;
  isVisible?: boolean; // 뷰포트 노출 여부 — false면 autoplay 중지 (기본값: true) BN001 에서 전달 받음
}

const groupItems = ($items: JSX.Element | JSX.Element[] | undefined): any => {
  const perView = 3; // 3개 = 1set

  // items가 단일 요소이거나 정의되지 않은 경우 실행 안함
  if (!$items || !Array.isArray($items)) {
    return [[$items]];
  }

  const result: JSX.Element[][] = [];
  const itemsCopy = [...$items];
  const itemsLength = itemsCopy.length;

  // 마지막 set이 perView 미만인 경우 잔여 슬롯을 1번 배너부터 순서대로 다시 채움
  // (단, 전체가 1set에 못 미치는 1~2개면 채우지 않고 그대로 가운데 정렬)
  const remainder = itemsLength % perView;
  if (remainder > 0 && itemsLength > perView) {
    const extraItems = Array.from(
      { length: perView - remainder },
      (_, i) => itemsCopy[i % itemsLength]
    );

    itemsCopy.push(...extraItems);
  }

  // 세트로 항목 그룹화
  for (let i = 0; i < itemsCopy.length; i += perView) {
    result.push(itemsCopy.slice(i, i + perView));
  }

  return result;
};

const MainBannerSwiperType2 = ({
  items,
  isDesktop = false,
  onSlideChange,
  autoPlayEnabled = true,
  onTransitionEnd,
  isVisible = true,
}: IProps) => {
  const [swiperReady, setSwiperReady] = useState(false);
  const swiperRef = useRef<SwiperClass | null>(null);
  const [isAutoPlay, setIsAutoPlay] = useState(false);

  // PC버전 세트구성
  const groupedItems = useMemo(
    () => (isDesktop && items ? groupItems(items) : items),
    [isDesktop, items]
  );

  // PC버전 progressbar 다시 계산
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !isDesktop || !swiperReady) return;

    const handleProgress = () => {
      const currentIndex = swiper.realIndex % groupedItems.length;
      const calculatedProgress = (currentIndex + 1) / groupedItems.length;

      const progressBar = document.querySelector(
        '.cards-type-swiper-progressbar .swiper-pagination-progressbar-fill'
      );

      if (progressBar && progressBar instanceof HTMLElement) {
        progressBar.style.transform = `translate3d(0px, 0px, 0px) scaleX(${calculatedProgress}) scaleY(1)`;
        progressBar.style.transitionDuration = `300ms`;
      }
    };

    handleProgress();

    swiper.on('slideChange', handleProgress);

    return () => {
      if (swiper) {
        swiper.off('slideChange', handleProgress);
      }
    };
  }, [isDesktop, groupedItems.length, swiperReady]);

  // PC버전 autoplay 컨트롤
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !isDesktop || !swiperReady) return;

    const handleProgress = () => {
      if (isAutoPlay) {
        swiper.autoplay.stop();
      } else {
        swiper.autoplay.start();
      }
    };

    handleProgress();

    swiper.on('transitionEnd', handleProgress);

    return () => {
      if (swiper) {
        swiper.off('transitionEnd', handleProgress);
      }
    };
  }, [isAutoPlay, isDesktop, swiperReady]);

  // PC버전 초기에 swiperRef null값 방지를 위해 상태관리
  useEffect(() => {
    if (isDesktop && swiperRef.current) {
      setSwiperReady(true);
    }
  }, [isDesktop]);

  // 모바일 autoplay 외부 제어: autoPlayEnabled·isVisible 모두 true일 때만 autoplay 시작
  useEffect(() => {
    if (isDesktop) return;
    const swiper = swiperRef.current;
    if (!swiper) return;
    if (autoPlayEnabled && isVisible) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }, [autoPlayEnabled, isDesktop, isVisible]);

  // PC autoplay 가시성 제어: 화면 밖으로 나가면 중지, 돌아오면 사용자 설정 기준으로 재개
  useEffect(() => {
    if (!isDesktop) return;
    const swiper = swiperRef.current;
    if (!swiper || !swiperReady) return;
    if (!isVisible) {
      swiper.autoplay.stop();
    } else if (!isAutoPlay) {
      // isAutoPlay=true 는 사용자가 autoplay를 껐음을 의미하므로 재시작하지 않음
      swiper.autoplay.start();
    }
  }, [isDesktop, isVisible, isAutoPlay, swiperReady]);

  // PC버전 마우스 아웃 시 사용자 autoplay 설정 기준으로 복구
  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !isDesktop) return;

    const swiperEl = swiper.el;

    if (swiperEl) {
      const handleMouseLeave = () => {
        setTimeout(() => {
          if (isAutoPlay) {
            swiper.autoplay.stop();
          } else {
            swiper.autoplay.start();
          }
        }, 30);
      };

      swiperEl.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        swiperEl.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [isAutoPlay, isDesktop]);

  const itemsLength = (() => {
    if (Array.isArray(items)) return items.length;
    if (items) return 1;
    return 0;
  })();

  const paramMo: any = {
    autoplay: autoPlayEnabled
      ? { delay: 3000, disableOnInteraction: false }
      : false,
    spaceBetween: 0,
    virtual: !!(Array.isArray(items) && items.length > 5),
    pagination: {
      el: '.main-top-banner-fraction',
      type: 'fraction',
      formatFractionCurrent: (number: number) =>
        String(((number - 1) % itemsLength) + 1).padStart(2, '0'),
      formatFractionTotal: () => String(itemsLength).padStart(2, '0'),
      renderFraction: (currentClass: string, totalClass: string) =>
        `<span class="${currentClass}"></span> | <span class="${totalClass}"></span>`,
    },
    paginationEl: (
      <SwiperPagination
        type="fraction03"
        className="main-top-banner-fraction"
      />
    ),
    onSwiper: (swiper: SwiperClass) => {
      if (swiper) {
        swiperRef.current = swiper;
        setSwiperReady(true);
      }
    },
  };

  const paramPc: any = {
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    spaceBetween: 18,
    slidesPerView: 'auto',
    preventInteractionOnTransition: true,
    navigation: {
      prevEl: '.cards-type-swiper .swiper-button-prev',
      nextEl: '.cards-type-swiper .swiper-button-next',
    },
    navigationEl: (
      <SwiperNavigation
        type="bottom01"
        size="medium"
        className="cards-type-swiper"
        onClickAutoplayControlBtn={state => {
          if (swiperRef.current) {
            setIsAutoPlay(state);
            if (state) {
              swiperRef.current?.autoplay.stop();
            } else {
              swiperRef.current?.autoplay.start();
            }
          }
        }}
      />
    ),
    pagination: {
      el: '.cards-type-swiper-progressbar',
      type: 'progressbar',
    },
    keyboard: {
      enabled: true,
    },
    paginationEl: (
      <SwiperPagination
        type="progressbar01"
        className="cards-type-swiper-progressbar"
      />
    ),
    onSwiper: (swiper: SwiperClass) => {
      if (swiper) {
        swiperRef.current = swiper;
        setSwiperReady(true);
      }
    },
  };

  return (
    <S.MainBannerSwiperType2>
      <ItemsSwiper
        speed={400}
        centeredSlides
        loopedPerView={3}
        loopAdditionalSlides={1}
        onSlideChange={onSlideChange}
        onTransitionEnd={onTransitionEnd}
        {...(isDesktop ? paramPc : paramMo)}
      >
        {isDesktop && Array.isArray(groupedItems)
          ? groupedItems.map((group: JSX.Element[], index: number) => (
              <React.Fragment key={index}>{group}</React.Fragment>
            ))
          : items}
      </ItemsSwiper>
    </S.MainBannerSwiperType2>
  );
};

export default memo(MainBannerSwiperType2);
